import React, { useMemo, useState } from 'react';

export type SortMode = 'most' | 'least';

export interface StatisticsListProps {
  title: string;
  icon?: React.ReactNode;
  data: Record<string, number>;
  badgeClassName?: string;
  emptyMessage?: string;
}

const ITEMS_PER_PAGE = 5;

const formatKey = (key: string): string =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const formatCity = (city: string | null | undefined): string => {
  const value = city && city.trim().length > 0 ? city : 'Unknown';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const StatisticsList: React.FC<StatisticsListProps> = ({
  title,
  icon,
  data,
  badgeClassName,
  emptyMessage = 'No data available.',
}) => {
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('most');
  const [page, setPage] = useState(1);

  const entries = useMemo(
    () => Object.entries(data || {}),
    [data]
  );

  const filteredAndSorted = useMemo(() => {
    let result = entries;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(([key]) => formatKey(key).toLowerCase().includes(q) || formatCity(key).toLowerCase().includes(q));
    }

    result = [...result].sort((a, b) => {
      const diff = b[1] - a[1];
      return sortMode === 'most' ? diff : -diff;
    });

    return result;
  }, [entries, search, sortMode]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / ITEMS_PER_PAGE));

  const paginated = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredAndSorted.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSorted, page]);

  // Reset page when search or sort changes to avoid empty pages
  React.useEffect(() => {
    setPage(1);
  }, [search, sortMode]);

  const isCityMode = title.toLowerCase().includes('city');

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            {icon && <span className="me-2">{icon}</span>}
            <h6 className="mb-0 text-uppercase text-muted small">{title}</h6>
          </div>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
            >
              <option value="most">Most</option>
              <option value="least">Least</option>
            </select>
          </div>
        </div>

        <div className="mb-3">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {filteredAndSorted.length === 0 ? (
          <p className="text-muted mb-0">{emptyMessage}</p>
        ) : (
          <>
            {paginated.map(([key, value]) => {
              const label = isCityMode ? formatCity(key) : formatKey(key);
              return (
                <div
                  key={key}
                  className="mb-2 d-flex justify-content-between align-items-center"
                >
                  <span className="fw-semibold">{label}</span>
                  <span className={badgeClassName}>{value}</span>
                </div>
              );
            })}

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Page {page} of {totalPages}
                </small>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StatisticsList;
